// pages/TermsOfServicePage.jsx
import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Link as MLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tooltip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const GOVERNING_LAW_STATE = "Wisconsin";
const PAGE_MAX_WIDTH = 1160;
const BODY_TEXT = "#0f172a";
const MUTED_TEXT = "#64748b";

const TOC_ITEMS = [
  { href: "#acceptance", text: "1. Acceptance" },
  { href: "#eligibility", text: "2. Eligibility & Accounts" },
  { href: "#verification", text: "3. ID Verification" },
  { href: "#verification-eligibility", text: "3a. Verification Eligibility" },
  { href: "#content", text: "4. Your Content & Conduct" },
  { href: "#permitted", text: "5. Permitted Uses" },
  { href: "#prohibited", text: "6. Prohibited Uses" },
  { href: "#payments", text: "7. Payments/Donations" },
  { href: "#ip", text: "8. IP & Licenses" },
  { href: "#thirdparty", text: "9. Third-Party Services" },
  { href: "#disclaimers", text: "10. Disclaimers" },
  { href: "#liability", text: "11. Limitation of Liability" },
  { href: "#indemnity", text: "12. Indemnification" },
  { href: "#termination", text: "13. Termination" },
  { href: "#governinglaw", text: "14. Governing Law" },
  { href: "#changes", text: "15. Changes to These Terms" },
  { href: "#contact", text: "Contact" },
];

const SECTION_STYLE = { scrollMarginTop: 96 };

const TOS_MARKDOWN = `# CivixThread — Terms of Service

## 1. Acceptance
By accessing or using CivixThread (the "Service"), you agree to these Terms of Service and our Privacy Policy. If you don't agree, don't use the Service.

## 2. Eligibility & Accounts
You must be at least 18 years old to use CivixThread. You are responsible for maintaining the security of your account (including keeping your password secure) and for all activity under your account. We use Firebase Authentication for login.

## 3. Identity Verification
To keep discourse authentic and reduce spam, certain actions (e.g., posting, voting, reporting) may require successful verification through Stripe Identity. Stripe processes ID images/biometrics on our behalf. We receive verification results and related metadata; we do not store ID images.

### 3a. Identity Verification Eligibility
CivixThread uses third-party identity verification services to confirm user identity. While these services may successfully verify certain forms of government-issued identification, including limited term or temporary identification documents, CivixThread reserves the right to:
- Review verification status at its sole discretion.
- Revoke or remove verification badges or status.
- Request additional documentation.
- Suspend or restrict access to verification-dependent features.

If the submitted identification does not meet CivixThread's internal eligibility criteria for participation in certain civic engagement features. Verification status does not guarantee eligibility for all platform functionality and may be subject to reevaluation at any time.

## 4. Your Content & Conduct
You retain ownership of content you post. You grant us a worldwide, royalty-free license to host, display, and distribute your content solely to operate the Service. You're responsible for your content and for obtaining any necessary permissions.

**Acceptable Use & Community Conduct**

You agree not to:

- Impersonate or misrepresent your identity or affiliation.
- Engage in harassment, threats, hate speech, intimidation, or doxxing.
- Post spam, unsolicited messages/comments, astroturfing, or coordinated inauthentic behavior.
- Upload or share illegal content or engage in unlawful activities.
- Use the Service in violation of applicable laws or court orders.
- Attempt to hack, modify, disrupt, or bypass security measures of the Service.
- Access non-public/private APIs or crawl, scrape, or cache content at scale.
- Upload malware (worms, viruses) or any destructive code.
- Infringe intellectual property rights (including copyright or trademarks).
- Submit fraudulent information or misuse identity verification processes.
- Upload nude, partially nude, or sexually explicit/suggestive content.

Violations may result in content removal, feature restrictions, or account suspension/termination. You remain solely responsible for the content you submit or display through the Service.

## 5. Permitted Uses
You may use the Service to follow legislation, track representatives, participate in verified civic conversations, and access tools we provide for personal, non-commercial use unless otherwise authorized.

## 6. Prohibited Uses
You agree not to:
- Reverse engineer, scrape at scale, or circumvent technical protections.
- Use bots or automation to create accounts, post, or manipulate discussions.
- Upload malicious code or interfere with the Service's security or availability.
- Misuse verification processes or submit fraudulent information.
- Use the Service for political campaigning in violation of applicable laws.

## 7. Payments / Donations
If you purchase features or make donations, you authorize our payment processor (e.g., Stripe) to charge your selected payment method. All billing terms (pricing, refunds, renewals) will be presented at checkout.

## 8. Intellectual Property & Licenses
CivixThread, our logos, and the Service's look-and-feel are our IP. You may not use our marks without permission. Where we provide APIs or data, separate terms may apply.

## 9. Third-Party Services
We rely on providers like Stripe Identity, Firebase/Google Cloud, and others. Their terms and privacy policies apply in addition to ours. We're not responsible for third-party websites or services.

## 10. Disclaimers
The Service is provided "as is" and "as available." We don't promise uninterrupted or error-free operation. Information (e.g., bill data) may come from third-party sources and could be incomplete or inaccurate. CivixThread may also analyze anonymized or aggregated platform activity (such as general participation or sentiment trends) to improve service features or support civic engagement initiatives.

## 11. Limitation of Liability
To the maximum extent permitted by law, CivixThread and its affiliates won't be liable for indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or revenues.

## 12. Indemnification
You agree to defend, indemnify, and hold harmless CivixThread and its affiliates from claims arising out of your use of the Service or violation of these Terms.

## 13. Suspension & Termination
We may suspend or terminate access if you violate these Terms, harm other users, or risk the integrity or security of the Service. You may stop using the Service at any time.

## 14. Governing Law & Dispute Resolution
These Terms are governed by the laws of the United States and the State of ${GOVERNING_LAW_STATE}, without regard to conflict-of-law rules. Courts located in ${GOVERNING_LAW_STATE} will have exclusive jurisdiction, unless otherwise required by law.

## 15. Changes to These Terms
We may update these Terms to reflect changes in our Service or the law. We'll post updates here and revise the "Last updated" date. Material changes may be announced in-app or by email.

## Contact
Questions about these Terms? Email us at support@civixthread.com.
`;

export default function TermsOfServicePage() {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(TOS_MARKDOWN).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: BODY_TEXT,
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            Terms of Service
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED_TEXT }}>
            Please review these terms before using CivixThread.
          </Typography>
          </Box>
          <Tooltip title={copied ? "Copied!" : "Copy as Markdown"} arrow>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              onClick={handleCopy}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontWeight: 500,
                color: copied ? "#047857" : BODY_TEXT,
                borderColor: copied ? "#047857" : "#94a3b8",
                bgcolor: copied ? "#f0fdf4" : "#fff",
                whiteSpace: "nowrap",
                flexShrink: 0,
                minHeight: 36,
                px: 1.5,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#047857",
                  color: "#047857",
                  bgcolor: "#f0fdf4",
                  boxShadow: "none",
                },
              }}
            >
              {copied ? "Copied!" : "Copy as Markdown"}
            </Button>
          </Tooltip>
          </Box>

          {/* Two-column: sidebar left, content right */}
          <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          {/* Sticky sidebar — left-aligned */}
          <Box
            component="nav"
            sx={{
              display: { xs: "none", lg: "block" },
              width: 220,
              flexShrink: 0,
              position: "sticky",
              top: 96,
              borderRight: "1px solid",
              borderColor: "divider",
              pr: 4,
              py: 1,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: MUTED_TEXT,
                letterSpacing: 1,
                fontSize: 11,
                display: "block",
                mb: 1.5,
              }}
            >
              On this page
            </Typography>
            <List dense disablePadding sx={{ "& .MuiListItem-root": { px: 0 } }}>
              {TOC_ITEMS.map((item) => (
                <ListItem key={item.href} disableGutters sx={{ py: 0.4 }}>
                  <ListItemText
                    primary={
                      <MLink
                        href={item.href}
                        underline="hover"
                        sx={{
                          fontSize: 14,
                          color: BODY_TEXT,
                          fontWeight: 500,
                          "&:hover": { color: "#047857" },
                          pl: item.href === "#verification-eligibility" ? 2 : 0,
                        }}
                      >
                        {item.text}
                      </MLink>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Main content */}
          <Box sx={{ flex: 1, maxWidth: 720, minWidth: 0 }}>
            {/* Summary */}
            <Box
              sx={{
                p: 3,
                mb: 5,
                bgcolor: "#fff",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "#e2e8f0",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1 }}>
                Summary
              </Typography>
              <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, fontSize: 15 }}>
                CivixThread helps people follow legislation and engage with representatives. By using our product,
                you agree to be a real person, follow our civic-discourse rules, and comply with the law. We may
                require ID verification through Stripe Identity for features like posting or voting. We can suspend
                accounts for abuse or policy violations.
              </Typography>
            </Box>

            {/* Mobile ToC */}
            <Box sx={{ display: { lg: "none" }, mb: 4 }}>
              <Typography variant="overline" sx={{ fontWeight: 600, color: MUTED_TEXT, letterSpacing: 1, fontSize: 11 }}>
                On this page
              </Typography>
              <List dense sx={{ mt: 0.5 }}>
                {TOC_ITEMS.map((item) => (
                  <ListItem key={item.href} disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <MLink href={item.href} underline="hover" sx={{ color: "#047857", fontSize: 14 }}>
                          {item.text}
                        </MLink>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Sections */}
            <Box component="article" sx={{ "& section": { mb: 4 } }}>
              <section id="acceptance" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  1. Acceptance
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  By accessing or using CivixThread (the "Service"), you agree to these Terms of Service and our{" "}
                  <MLink href="/privacy-policy" sx={{ color: "#047857", fontWeight: 500 }}>Privacy Policy</MLink>.
                  If you don't agree, don't use the Service.
                </Typography>
              </section>

              <section id="eligibility" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  2. Eligibility & Accounts
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  You must be at least 18 years old to use CivixThread. You are
                  responsible for maintaining the security of your account (including keeping your password secure)
                  and for all activity under your account. We use Firebase Authentication for login.
                </Typography>
              </section>

              <section id="verification" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  3. Identity Verification
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  To keep discourse authentic and reduce spam, certain actions (e.g., posting, voting, reporting)
                  may require successful verification through{" "}
                  <MLink href="https://stripe.com/identity" target="_blank" rel="noopener noreferrer" sx={{ color: "#047857", fontWeight: 500 }}>
                    Stripe Identity
                  </MLink>
                  . Stripe processes ID images and biometric data on our behalf. CivixThread may have limited
                  access to verification information through Stripe's secure dashboard for review and support
                  purposes, but we do not download or store ID images or biometric data in our own systems.
                </Typography>
              </section>

              <section id="verification-eligibility" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  3a. Identity Verification Eligibility
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1 }}>
                  CivixThread uses third-party identity verification services to confirm user identity. While
                  these services may successfully verify certain forms of government-issued identification,
                  including limited term or temporary identification documents, CivixThread reserves the right to:
                </Typography>
                <List dense sx={{ mb: 1, pl: 1 }}>
                  {[
                    "Review verification status at its sole discretion.",
                    "Revoke or remove verification badges or status.",
                    "Request additional documentation.",
                    "Suspend or restrict access to verification-dependent features.",
                  ].map((t) => (
                    <ListItem key={t} sx={{ py: 0.2, display: "list-item", listStyleType: "disc" }}>
                      <ListItemText primary={t} primaryTypographyProps={{ variant: "body2", sx: { color: BODY_TEXT, lineHeight: 1.6 } }} />
                    </ListItem>
                  ))}
                </List>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  If the submitted identification does not meet CivixThread's internal eligibility criteria for
                  participation in certain civic engagement features. Verification status does not guarantee
                  eligibility for all platform functionality and may be subject to reevaluation at any time.
                </Typography>
              </section>

              <section id="content" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  4. Your Content & Conduct
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1 }}>
                  You retain ownership of content you post. You grant us a worldwide, royalty-free license to
                  host, display, and distribute your content solely to operate the Service. You're responsible
                  for your content and for obtaining any necessary permissions.
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1 }}>
                  Acceptable Use & Community Conduct
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 0.5 }}>
                  You agree not to:
                </Typography>
                <List dense sx={{ pl: 1, mb: 1 }}>
                  {[
                    "Impersonate or misrepresent your identity or affiliation.",
                    "Engage in harassment, threats, hate speech, intimidation, or doxxing.",
                    "Post spam, unsolicited messages/comments, astroturfing, or coordinated inauthentic behavior.",
                    "Upload or share illegal content or engage in unlawful activities.",
                    "Use the Service in violation of applicable laws or court orders.",
                    "Attempt to hack, modify, disrupt, or bypass security measures of the Service.",
                    "Access non-public/private APIs or crawl, scrape, or cache content at scale.",
                    "Upload malware (worms, viruses) or any destructive code.",
                    "Infringe intellectual property rights (including copyright or trademarks).",
                    "Submit fraudulent information or misuse identity verification processes.",
                    "Upload nude, partially nude, or sexually explicit/suggestive content.",
                  ].map((t) => (
                    <ListItem key={t} sx={{ py: 0.2, display: "list-item", listStyleType: "disc" }}>
                      <ListItemText primary={t} primaryTypographyProps={{ variant: "body2", sx: { color: BODY_TEXT, lineHeight: 1.6 } }} />
                    </ListItem>
                  ))}
                </List>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  Violations may result in content removal, feature restrictions, or account suspension/termination.
                  You remain solely responsible for the content you submit or display through the Service.
                </Typography>
              </section>

              <section id="permitted" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  5. Permitted Uses
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  You may use the Service to follow legislation, track representatives, participate in verified
                  civic conversations, and access tools we provide for personal, non-commercial use unless
                  otherwise authorized.
                </Typography>
              </section>

              <section id="prohibited" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  6. Prohibited Uses
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 1 }}>
                  You agree not to:
                </Typography>
                <List dense sx={{ pl: 1 }}>
                  {[
                    "Reverse engineer, scrape at scale, or circumvent technical protections.",
                    "Use bots or automation to create accounts, post, or manipulate discussions.",
                    "Upload malicious code or interfere with the Service's security or availability.",
                    "Misuse verification processes or submit fraudulent information.",
                    "Use the Service for political campaigning in violation of applicable laws.",
                  ].map((t) => (
                    <ListItem key={t} sx={{ py: 0.2, display: "list-item", listStyleType: "disc" }}>
                      <ListItemText primary={t} primaryTypographyProps={{ variant: "body2", sx: { color: BODY_TEXT, lineHeight: 1.6 } }} />
                    </ListItem>
                  ))}
                </List>
              </section>

              <section id="payments" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  7. Payments / Donations
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  If you purchase features or make donations, you authorize our payment processor (e.g., Stripe)
                  to charge your selected payment method. All billing terms (pricing, refunds, renewals) will be
                  presented at checkout.
                </Typography>
              </section>

              <section id="ip" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  8. Intellectual Property & Licenses
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  CivixThread, our logos, and the Service's look-and-feel are our IP. You may not use our marks
                  without permission. Where we provide APIs or data, separate terms may apply.
                </Typography>
              </section>

              <section id="thirdparty" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  9. Third-Party Services
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  We rely on providers like Stripe Identity, Firebase/Google Cloud, and others. Their terms and
                  privacy policies apply in addition to ours. We're not responsible for third-party websites or
                  services.
                </Typography>
              </section>

              <section id="disclaimers" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  10. Disclaimers
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  The Service is provided "as is" and "as available." We don't promise uninterrupted or
                  error-free operation. Information (e.g., bill data) may come from third-party sources and could
                  be incomplete or inaccurate. CivixThread may also analyze anonymized or aggregated platform
                  activity (such as general participation or sentiment trends) to improve service features or
                  support civic engagement initiatives.
                </Typography>
              </section>

              <section id="liability" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  11. Limitation of Liability
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  To the maximum extent permitted by law, CivixThread and its affiliates won't be liable for
                  indirect, incidental, special, consequential, or punitive damages, or any loss of data,
                  profits, or revenues.
                </Typography>
              </section>

              <section id="indemnity" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  12. Indemnification
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  You agree to defend, indemnify, and hold harmless CivixThread and its affiliates from claims
                  arising out of your use of the Service or violation of these Terms.
                </Typography>
              </section>

              <section id="termination" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  13. Suspension & Termination
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  We may suspend or terminate access if you violate these Terms, harm other users, or risk the
                  integrity or security of the Service. You may stop using the Service at any time.
                </Typography>
              </section>

              <section id="governinglaw" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  14. Governing Law & Dispute Resolution
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  These Terms are governed by the laws of the United States and the State of {GOVERNING_LAW_STATE},
                  without regard to conflict-of-law rules. Courts located in {GOVERNING_LAW_STATE} will have
                  exclusive jurisdiction, unless otherwise required by law.
                </Typography>
              </section>

              <section id="changes" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  15. Changes to These Terms
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7 }}>
                  We may update these Terms to reflect changes in our Service or the law. We'll post updates here
                  and revise the "Last updated" date. Material changes may be announced in-app or by email.
                </Typography>
              </section>

              <section id="contact" style={SECTION_STYLE}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: BODY_TEXT, mb: 1.5 }}>
                  Contact
                </Typography>
                <Typography sx={{ color: BODY_TEXT, lineHeight: 1.7, mb: 2 }}>
                  Questions about these Terms? Email us at{" "}
                  <MLink href="mailto:support@civixthread.com" sx={{ color: "#047857", fontWeight: 500 }}>
                    support@civixthread.com
                  </MLink>
                  .
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
                  href="/"
                  sx={{
                    bgcolor: "#047857",
                    color: "#ffffff",
                    "&:hover": { bgcolor: "#065f46", color: "#ffffff" },
                  }}
                >
                  Back to home
                </Button>
              </section>
            </Box>
          </Box>
        </Box>
        </Box>
      </Container>
    </Box>
  );
}
